import { gql } from '@apollo/client';
import {
  AspectRatio,
  Box,
  Button,
  HStack,
  Image,
  Spinner,
  Square,
  Text,
  Tooltip,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import { isEmpty, isNil } from 'lodash';
import { DateTime } from 'luxon';
import { CSSProperties, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '~auth/useAuth';
import { ErrorIcon, SyncIcon, WarningIcon } from '~components/ui/icons';
import {
  ScreenshotDisabledIllustration,
  ScreenshotEmptyIllustration,
} from '~components/ui/illustrations';
import { ScreenshotErrorIllustration } from '~components/ui/illustrations/ScreenshotErrorIllustration';
import { RelativeTime, SHORT_FORMAT_TIME } from '~components/ui/RelativeTime';
import { Orientation } from '~graphql/__generated__/types';
import { useAnalyticsReporter } from '~utils/analytics';
import { fromError } from '~utils/errors';
import {
  DisplayScreenshot_DisplayFragment,
  useCaptureScreenshotMutation,
} from './__generated__/DisplayScreenshot.graphql';

interface Props {
  display: DisplayScreenshot_DisplayFragment;
}

export function DisplayScreenshot({ display }: Props) {
  const toast = useToast();
  const [captureScreenshot] = useCaptureScreenshotMutation();
  const analytics = useAnalyticsReporter();
  const { verifyUserPermissions } = useAuth();
  const { t } = useTranslation();
  const onClick = async () => {
    try {
      await captureScreenshot({
        variables: {
          input: {
            displayId: display.id,
          },
        },
      });

      analytics.track('displayPreviewUpdate');
    } catch (error) {
      toast({
        status: 'error',
        title: t('captureScreenErr'),
        description: fromError(error, 'CaptureScreenshot', {
          DISPLAY_OFFLINE: t('captureScreenErrDesc'),
          SCREENSHOT_NOT_AVAILABLE: t('screenshotUnavailableDesc'),
        }),
      });
    }
  };

  const screenshot = display.screenshot;
  const screenshotTypeName = screenshot?.__typename;
  const syncButtonLabel =
    screenshotTypeName === 'ScreenshotEmpty'
      ? t('captureScreen')
      : screenshotTypeName === 'ScreenshotPending'
      ? t('updating')
      : '';

  //render logic corresponding to ScreenshotTypeName
  const isScreenshotNil = isNil(screenshot);
  const isScreenshotEmpty = screenshotTypeName === 'ScreenshotEmpty';
  const isScreenshotPending = screenshotTypeName === 'ScreenshotPending';
  const isScreenshotResolved = screenshotTypeName === 'ScreenshotResolved';
  const isScreenshotRejected = screenshotTypeName === 'ScreenshotRejected';

  const hasDisplayScreenshotCreatePermission = !verifyUserPermissions([
    Permission.DisplayScreenshotCreate,
  ]);

  const screenshotDisabled = isScreenshotNil || hasDisplayScreenshotCreatePermission;
  const shouldRenderSyncButton =
    isScreenshotEmpty || isScreenshotPending || isScreenshotResolved || isScreenshotRejected;
  const shouldRenderTime = isScreenshotResolved && !isNil(screenshot?.createdAt);

  const orientation = display.orientation?.desired ?? display.orientation?.reported;
  const isPortraitOrientation = orientation === Orientation.Portrait;

  const aspectRatio = orientation === Orientation.Portrait ? 9 / 16 : 16 / 9;

  return (
    <>
      <AspectRatio
        ratio={aspectRatio}
        maxW={isPortraitOrientation ? '248px' : '443px'}
        margin="0 auto"
      >
        <Box
          borderRadius="base"
          bgGradient={
            screenshotDisabled
              ? 'linear(to-tr, gray.50, gray.50)'
              : 'linear(to-tr, blue.700, blue.950)'
          }
        >
          <ScreenshotImage
            aspectRatio={aspectRatio}
            src={screenshot?.url ?? undefined}
            renderOverlay={isScreenshotPending}
            icon={
              screenshotDisabled ? (
                <ScreenshotDisabledIllustration width="140px" height="140px" />
              ) : isScreenshotEmpty ? (
                <ScreenshotEmptyIllustration width="140px" height="140px" />
              ) : isScreenshotRejected ? (
                <ScreenshotErrorIllustration width="140px" height="140px" />
              ) : isScreenshotPending ? (
                <Spinner color="white" />
              ) : null
            }
          />
        </Box>
      </AspectRatio>
      <ScreenshotFooter>
        {hasDisplayScreenshotCreatePermission ? (
          <HStack>
            <WarningIcon color="orange.400" width="24px" height="24px" />
            <Text fontSize="sm">{t('noPermissionsScreenCapture')}</Text>
          </HStack>
        ) : screenshotDisabled ? (
          <HStack>
            <WarningIcon color="orange.400" width="24px" height="24px" />
            <Text fontSize="sm">{t('noScreenCapture')}</Text>
          </HStack>
        ) : isScreenshotRejected ? (
          <HStack>
            <ErrorIcon color="red.400" width="24px" height="24px" />
            <Text fontSize="sm">{t('captureScreenErr')}</Text>
          </HStack>
        ) : shouldRenderTime ? (
          <RelativeTime
            ts={screenshot.createdAt}
            format={SHORT_FORMAT_TIME}
            render={(time) => {
              const date = DateTime.fromISO(screenshot.createdAt);
              const localTime = date.toFormat('dd-MM-yyyy hh:mm a');
              const utcTime = date.toUTC().toFormat('dd-MM-yyyy hh:mm a');

              return (
                <Tooltip
                  bg="gray.700"
                  color="white"
                  fontSize="sm"
                  p={2}
                  borderRadius="md"
                  placement="bottom-end"
                  offset={[175, 5]}
                  label={
                    <VStack align="start" spacing={0}>
                      <Text>
                        {t('local')}: {localTime}
                      </Text>
                      <Text>
                        {t('UTC')}: {utcTime}
                      </Text>
                    </VStack>
                  }
                >
                  <Text mr="2" fontSize="sm" cursor="pointer">
                    {t('updateTime', { time: time })}
                  </Text>
                </Tooltip>
              );
            }}
          />
        ) : (
          <Box />
        )}
        {!screenshotDisabled && shouldRenderSyncButton && (
          <SyncButton label={syncButtonLabel} onClick={onClick} disabled={isScreenshotPending} />
        )}
      </ScreenshotFooter>
    </>
  );
}

function ScreenshotImage({
  src,
  width = 446,
  aspectRatio = 16 / 9,
  renderOverlay,
  icon,
}: {
  src?: string;
  width?: number;
  aspectRatio?: number;
  renderOverlay?: boolean;
  icon?: ReactNode;
}) {
  const height = width / aspectRatio;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}"></svg>`;
  const sizer = (
    <img
      role="presentation"
      style={{
        display: 'block',
        width: '100%',
        borderRadius: 'base',
      }}
      src={`data:image/svg+xml;base64,${btoa(svg)}`}
    />
  );

  const absolutePositioning: CSSProperties = {
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
    borderRadius: 'base',
  };

  const hasSrc = !isNil(src);
  const hasIcon = !isNil(icon);

  return (
    <Box position="relative" overflow="hidden" display="block">
      {sizer}

      {hasSrc && (
        <>
          <Box style={{ ...absolutePositioning }} objectFit="cover" background="black" />
          <Image
            style={{ ...absolutePositioning }}
            objectFit="contain"
            src={src}
            alt="Display screenshot"
            backgroundColor="transparent"
            fallback={
              <Square style={{ ...absolutePositioning }}>
                <Spinner color="white" />
              </Square>
            }
          />
        </>
      )}

      {renderOverlay && (
        <Square
          style={{ ...absolutePositioning }}
          bgGradient="linear(to-b, rgb(24, 50, 88, 0.4), rgb(0, 36, 87, 0.9))"
          opacity="0.90"
        />
      )}

      {hasIcon && (
        <Square id="icon" style={{ ...absolutePositioning }}>
          {icon}
        </Square>
      )}
    </Box>
  );
}

function ScreenshotFooter({ children }: { children?: ReactNode }) {
  return (
    <HStack h="6" spacing="0" color="black" width="full" margin="12px auto">
      {children}
    </HStack>
  );
}

function SyncButton({
  label,
  disabled,
  onClick,
}: {
  label: string;
  disabled: boolean;
  onClick?: () => Promise<void>;
}) {
  const hasLabel = !isNil(label) && !isEmpty(label);
  return (
    <Button
      p="1"
      h="26px"
      colorScheme="blackAlpha"
      variant="ghost"
      isDisabled={disabled}
      onClick={onClick}
      _hover={{ backgroundColor: 'blackAlpha.500' }}
    >
      {hasLabel && (
        <Text mr="4" fontWeight="normal" color="gray.800" fontSize="sm">
          {label}
        </Text>
      )}
      <SyncIcon color="gray.800" />
    </Button>
  );
}

DisplayScreenshot.graphql = {
  fragments: {
    DisplayInfoSection_display: gql`
      fragment DisplayScreenshot_display on Display {
        id
        screenshot {
          __typename
          url
          createdAt
          ... on ScreenshotRejected {
            rejectedAt
            errorCode
          }
        }
        orientation {
          desired
          reported
        }
      }
    `,
  },
  mutations: {
    captureScreenshot: gql`
      mutation CaptureScreenshot($input: DisplayCaptureScreenshotInput!) {
        displayCaptureScreenshot(input: $input) {
          display {
            id
            screenshot {
              __typename
              url
            }
          }
        }
      }
    `,
  },
};
